/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AxiosError, AxiosResponse } from "axios";

import type { ApiRequestOptions } from "./ApiRequestOptions";
import { getDevSimulator } from "./request-dev-simulator";

const ANTI_BOT_ERROR_CODES: Set<string> = new Set([
  "ANTI_BOT_INVALID_TOKEN",
  "ANTI_BOT_CHALLENGE_REQUIRED",
  "ANTI_BOT_BLOCKED",
]);

const ANTI_BOT_KEYWORDS = ["anti-bot", "antibot"];

/**
 * Tests whether a single error field contains an anti-bot keyword (case-insensitive).
 *
 * @param value - The candidate field value to inspect
 * @returns True when the stringified value mentions "anti-bot"/"antibot"
 */
function fieldMatchesAntiBot(value: unknown): boolean {
  const text = String(value || "").toLowerCase();
  return ANTI_BOT_KEYWORDS.some((keyword) => text.includes(keyword));
}

/**
 * Detects an anti-bot signal across the RFC 7807 `detail`, `title`, and `type` fields.
 *
 * @param data - The error response body
 * @returns True when any of the inspected fields mentions anti-bot
 */
function hasAntiBotSignal(data: Record<string, unknown>): boolean {
  return [data.detail, data.title, data.type].some(fieldMatchesAntiBot);
}

/**
 * Classifies an error payload as anti-bot related, either by keyword signal or
 * by an explicit anti-bot `error_code`.
 *
 * @param data - The error response body
 * @returns True when the payload represents an anti-bot rejection
 */
export function isAntiBotError(data: any): boolean {
  if (!data || typeof data !== "object") return false;
  return hasAntiBotSignal(data) || ANTI_BOT_ERROR_CODES.has(data.error_code);
}

/**
 * Detects an OIDC token-related `error_code` (starts with "OIDC" and mentions "TOKEN").
 *
 * @param data - The error response body
 * @returns True when the error code identifies an OIDC token failure
 */
function hasOidcTokenErrorCode(data: any): boolean {
  const errorCode = String(data.error_code || "").toUpperCase();
  return errorCode.startsWith("OIDC") && errorCode.includes("TOKEN");
}

/**
 * Classifies an error payload as an OIDC token validation failure, matching either
 * the `oidcinvalidtoken` title or an OIDC token error code.
 *
 * @param data - The error response body
 * @returns True when the payload represents an OIDC token validation error
 */
export function isOidcTokenValidationError(data: any): boolean {
  if (!data || typeof data !== "object") return false;
  const title = String(data.title || "").toLowerCase();
  return title.includes("oidcinvalidtoken") || hasOidcTokenErrorCode(data);
}

/**
 * Detects a 403 whose `detail` indicates an invalid or expired credential.
 *
 * @param data - The error response body
 * @returns True when the detail mentions "invalid or expired"
 */
function is403WithExpiredDetail(data: any): boolean {
  if (!data?.detail) return false;
  const detail = String(data.detail).toLowerCase();
  return detail.includes('invalid or expired');
}

/**
 * Detects the explicit `"Token has expired."` detail payload.
 *
 * @param data - The error response body
 * @returns True when the detail is exactly the token-expired message
 */
function isExplicitTokenExpiredPayload(data: any): boolean {
  return data && typeof data === "object" && data.detail === "Token has expired.";
}

/**
 * Decides whether an HTTP status/data pair signals an expired credential: any 401,
 * or a 403 carrying an expired-detail body.
 *
 * @param status - The HTTP status code, if available
 * @param data - The error response body
 * @returns True when the status/data combination indicates expiry
 */
function isStatusExpired(status: number | undefined, data: any): boolean {
  if (status === 401) return true;
  return status === 403 && is403WithExpiredDetail(data);
}

/**
 * Determines whether an Axios error represents an expired token that warrants a
 * refresh-and-retry, explicitly excluding anti-bot rejections from that path.
 *
 * @param error - The Axios error to classify
 * @returns True when the error is a token-expiry that should trigger a refresh
 */
export function isTokenExpiredError(error: AxiosError<any>): boolean {
  const data = error.response?.data;
  if (isAntiBotError(data)) return false;
  if (isStatusExpired(error.response?.status, data)) return true;
  if (isOidcTokenValidationError(data)) return true;
  return isExplicitTokenExpiredPayload(data);
}

// Helper function to log anti-bot 403 diagnostics
/**
 * logAntiBot403 function.
 *
 * @returns {*} Nothing.
 */
export function logAntiBot403<T>(
  axiosError: AxiosError<T>,
  url: string,
  options: ApiRequestOptions,
  headers: Record<string, string>
): void {
  const data = axiosError.response?.data as Record<string, unknown>;
  console.error('[ANTIBOT] Backend rejected token (403)', {
    url,
    method: options.method,
    error_code: data?.error_code,
    detail: data?.detail,
    had_antibot_header: !!headers['X-Anti-Bot'],
    antibot_token_prefix: headers['X-Anti-Bot']?.slice(0, 20),
    antibot_token_length: headers['X-Anti-Bot']?.length ?? 0,
    response_headers: Object.fromEntries(
      Object.entries(axiosError.response?.headers || {}).filter(
        ([k]) => k.startsWith('x-') || k === 'date'
      )
    ),
  });
}

// Helper function to apply dev simulator hooks before sending the request.
// Preserves the original ordering: networkBroken check -> latency -> injected error.
/**
 * applyDevSimulator function.
 *
 * @returns {*} A simulated AxiosResponse, or null when no simulation applies.
 */
export async function applyDevSimulator<T>(): Promise<AxiosResponse<T> | null> {
  const sim = getDevSimulator();
  if (!sim) return null;

  const simState = sim.useStateSimulatorStore.getState();

  if (simState.networkBroken || simState.simulateDataState === 'error') {
    const err = new Error('Network Error') as any;
    err.code = 'ERR_NETWORK';
    throw err;
  }

  if (simState.latencyMs > 0) {
    await new Promise((r) => setTimeout(r, simState.latencyMs));
  }

  return maybeInjectHttpError<T>(sim, simState);
}

/**
 * Emits a synthetic HTTP error response when the dev simulator has a pending
 * injected error, consuming one from the configured count per invocation.
 *
 * @param sim - The active dev simulator instance
 * @param simState - The current injected-error status and remaining count
 * @returns A simulated error AxiosResponse, or null when nothing is injected
 */
function maybeInjectHttpError<T>(
  sim: ReturnType<typeof getDevSimulator>,
  simState: { injectedHttpError: number | null; injectedErrorCount: number }
): AxiosResponse<T> | null {
  if (!simState.injectedHttpError || simState.injectedErrorCount <= 0) return null;
  sim!.useStateSimulatorStore.getState().consumeInjectedError();
  return {
    data: { detail: `Simulated ${simState.injectedHttpError} error` },
    status: simState.injectedHttpError,
    statusText: 'Simulated Error',
    headers: {},
    config: {} as any,
  } as AxiosResponse<T>;
}
